import { useEffect, useState, useMemo } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Skeleton } from "../../components/ui/skeleton";
import { Sparkles, AlertTriangle, Clock } from "lucide-react";
import { daysAgoIST, formatISTDate, formatISTTime, todayIST } from "../../lib/datetime";
import { attendanceLabel, joinParts, lateCheckInCopy, NO_RECORD, UNKNOWN_STATUS } from "../../lib/vocabulary";
import { labelFor, roleBreakdown } from "../../lib/population";
import SelfCheckInCard from "./SelfCheckInCard";
import StickyHeading from "../../components/common/StickyHeading";
import TerritoryLabel from "../../components/common/TerritoryLabel";
import { StatusPill } from "./MyAttendancePage";

/** The order a status breakdown always reads in. Labels, not server keys. */
const LABEL_ORDER = [
  attendanceLabel("Present"),
  attendanceLabel("Half Day"),
  attendanceLabel("On Leave"),
  attendanceLabel("Absent"),
  attendanceLabel("Holiday"),
  UNKNOWN_STATUS,
  NO_RECORD,
];
/** Always stated, even at zero — a missing line reads as "no such people". */
const ALWAYS_SHOWN = [attendanceLabel("Present"), attendanceLabel("On Leave"), attendanceLabel("Absent")];



/** RC-31's history half: the team's attendance over a chosen window.
 *
 * The backend has served this since Block 4's windows work — `GET
 * /api/attendance` with from/to, team-scoped by the same visibility filter as
 * everything else — and no screen consumed it: the team view was today-only,
 * so "was Mukesh in on Tuesday?" had no answer short of the raw API. Rows
 * group by DAY, and a day with no record for a person is simply not a row —
 * Pattern A: absence of a record is never rendered as "Absent".
 *
 * The other half of RC-31 (a regularisation path for a missed check-in) is a
 * business flow nobody has decided — who may regularise, and what it writes —
 * and is deliberately NOT invented here (checkpoint rule).
 */
function TeamHistory() {
  const [from, setFrom] = useState(() => daysAgoIST(6));
  const [to, setTo] = useState(() => todayIST());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api.get("/attendance", { params: { from_date: from, to_date: to } })
      .then(({ data }) => { if (!cancelled) setRows(data); })
      .catch((e) => {
        // Over-limit refusals arrive here too, with the count and the advice
        // to narrow — served verbatim rather than rephrased.
        if (!cancelled) {
          setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
          setRows([]);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to]);

  const byDay = useMemo(() => {
    const groups = new Map();
    for (const r of rows) {
      if (!groups.has(r.date)) groups.set(r.date, []);
      groups.get(r.date).push(r);
    }
    return [...groups.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [rows]);

  return (
    <div className="bg-white border border-callisto rounded-xl p-4" data-testid="team-attendance-history">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-bold text-callisto-navy">History</p>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            aria-label="History from"
            className="h-9 rounded-lg border border-callisto px-2 text-xs"
            data-testid="attendance-history-from"
          />
          <span className="text-xs text-callisto-muted">to</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            aria-label="History to"
            className="h-9 rounded-lg border border-callisto px-2 text-xs"
            data-testid="attendance-history-to"
          />
        </div>
      </div>

      {loading ? (
        <Skeleton className="h-16 rounded-lg mt-3" />
      ) : error ? (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="attendance-history-error">
          {error}
        </div>
      ) : byDay.length === 0 ? (
        <p className="mt-3 text-sm text-callisto-muted" data-testid="attendance-history-empty">
          No attendance was captured in this window. A day with no record is a
          gap, not an absence.
        </p>
      ) : (
        <div className="mt-3 space-y-3" data-testid="attendance-history-days">
          {byDay.map(([day, dayRows]) => (
            <div key={day} data-testid={`attendance-history-day-${day}`}>
              <p className="text-xs font-bold text-callisto-muted">{formatISTDate(day)}</p>
              <div className="mt-1 divide-y divide-callisto/40">
                {dayRows.map((r) => (
                  <div key={r.id} className="py-1.5 flex items-center justify-between gap-2 text-sm" data-testid={`attendance-history-row-${r.id}`}>
                    <span className="truncate text-callisto-navy">{r.sales_officer_name}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      {r.check_in_time && (
                        <span className="text-xs text-callisto-muted">{formatISTTime(r.check_in_time)}</span>
                      )}
                      <StatusPill status={r.status} testId={`attendance-history-status-${r.id}`} />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TeamAttendancePage() {
  const { user } = useAuth();
  const [todayData, setTodayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    // The manager's OWN month is rendered by SelfCheckInCard below, which
    // fetches it. This page used to fetch it a second time and draw a second
    // calendar from it — one fact, two components, two requests.
    api.get("/attendance/today")
      .then(({ data }) => !cancelled && setTodayData(data))
      .catch((e) => !cancelled && setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [user?.id]);

  const members = useMemo(() => todayData?.members || [], [todayData]);
  const alerts = useMemo(() => members.filter((m) => m.absent_alert), [members]);

  /**
   * COUNT THE REPORTED STATUS, DO NOT SUBTRACT. "Not checked in" used to be
   * `members - present - onLeave`, which quietly folded Holiday, Half Day and
   * any status the app had not been taught into a claim that those people had
   * not checked in.
   */
  const byLabel = useMemo(() => {
    const m = {};
    for (const person of members) {
      const l = attendanceLabel(person.status);
      m[l] = (m[l] || 0) + 1;
    }
    return m;
  }, [members]);

  const breakdown = joinParts(
    LABEL_ORDER.filter((l) => ALWAYS_SHOWN.includes(l) || byLabel[l]).map(
      (l) => `${l} ${byLabel[l] || 0}`
    )
  );

  /**
   * WHO THIS IS A LIST OF. The set is whatever the backend put in scope —
   * Sales Officers, Sales Managers, the Admin Manager and, on most roles, the
   * viewer. `labelFor` refuses to name a role a mixed set is not made of; the
   * rows carry no `role`, so it says "people", which is the honest word.
   */
  const population = useMemo(() => roleBreakdown(members), [members]);
  const includesViewer = useMemo(
    () => Boolean(user?.id) && members.some((m) => m.id === user.id),
    [members, user?.id]
  );
  const populationLine = joinParts(
    [
      `${members.length} ${labelFor(population, { plural: members.length !== 1 })} in this view`,
      includesViewer ? "including you" : null,
    ],
    ", "
  );

  return (
    <div className="space-y-6" data-testid="team-attendance-page">
      <StickyHeading
        icon={Sparkles}
        label="Team Attendance"
        title="Today's team"
        subtitle={
          <span data-testid="team-attendance-subtitle">
            {loading
              ? "Loading…"
              : error
              ? "Today's team could not be loaded."
              : joinParts([formatISTDate(todayData?.date), populationLine, breakdown])}
          </span>
        }
        testId="team-attendance-heading"
      />

      <SelfCheckInCard />

      <TeamHistory />

      {alerts.length > 0 && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 text-orange-800 px-3 py-2.5 text-sm flex items-start gap-2" data-testid="attendance-alert">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <div>
            {/* No role noun — the set is whoever the endpoint returned. */}
            <p>
              <span className="font-bold">{alerts.length}</span>{" "}
              {labelFor(roleBreakdown(alerts), { plural: alerts.length !== 1 })}{" "}
              {alerts.length === 1 ? "has" : "have"} no check-in captured yet today:{" "}
              <span className="font-semibold">{alerts.map((a) => a.name).join(", ")}</span>
            </p>
            {/* The hour is the Director's, held in Settings and served on this
                payload as `alert_hour_label`. `lateCheckInCopy` falls back to a
                sentence with no hour in it rather than inventing "10:00 AM",
                which is what the string here used to hardcode (RC-13). */}
            <p className="text-[11px] mt-0.5 opacity-90" data-testid="attendance-alert-criterion">
              {lateCheckInCopy(todayData?.alert_hour_label)}
            </p>
          </div>
        </div>
      )}

      {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
      ) : error ? null : members.length === 0 ? (
        <div className="bg-white border border-callisto rounded-xl p-6 text-center text-sm text-callisto-muted" data-testid="team-attendance-empty">
          No team members.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="team-attendance-list">
          {members.map((m) => (
            <div key={m.id} className="bg-white border border-callisto rounded-xl p-4" data-testid={`team-member-${m.id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-bold text-callisto-navy truncate">{m.name}</p>
                <StatusPill status={m.status} testId={`team-member-status-${m.id}`} />
              </div>
              <p className="text-[11px] text-callisto-muted mt-1">
                <TerritoryLabel
                  value={m.territory}
                  unmigrated={m.territory_unmigrated}
                  review="icon"
                  testId={`team-member-territory-${m.id}`}
                />
              </p>
              {m.check_in_time && (
                <p className="text-xs text-callisto-body mt-1 flex items-center gap-1">
                  <Clock size={11} /> in {formatISTTime(m.check_in_time)}
                  {m.check_out_time && <> · out {formatISTTime(m.check_out_time)}</>}
                </p>
              )}
              {m.absent_alert && (
                <p className="text-[11px] mt-1 text-orange-700 font-semibold flex items-center gap-1">
                  <AlertTriangle size={11} /> No check-in captured yet
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
